import React, { useState } from "react";
import { Container, Row, Table } from "reactstrap";
import { CenteredSpinner } from "../Common/CenteredSpinner";
import KaAlert from "../Common/KaAlert";
import useDisplayMessage from "../Common/useDisplayMessage";
import FilterInput, { textSatisfiesFilter } from "../FilterInput";
import { Button } from "react-bootstrap";
import useGetTransports, { TransportDto } from "../../requests/useGetTransports";
import PermissionButton from "../UserAccess/PermissionButton";
import { CreateEditTransport } from "./CreateEditTransport";
import { modalMode } from "../Common/EntityModal";
import useRemoveTransport from "../../requests/useRemoveTransport";
import Confirm from "../Common/Confirm";
import { useNavigate } from "react-router-dom";
import EditViewButton from "../UserAccess/EditViewButton";
import { usePermission } from "../UserAccess/usePermission";
import { SiteFilterPill, useSiteFilter } from "../UserAccess/SiteScope/SiteFilterPill";
import { SiteColumnCell, SiteColumnHeader } from "../Common/SiteColumn";
import { ActionsColumnHeader } from "../Common/ActionsColumn";

type PopupState =
  {
    state: "None";
    transport?: null;
  } |
  {
    state: "AddEditTransport";
    transport: TransportDto | null;
  };

const alphabeticallyByName = (a: TransportDto, b: TransportDto) => a.name.localeCompare(b.name);

const TransportPage = () => {
  const navigate = useNavigate();
  const displayMessage = useDisplayMessage();
  const [popupState, setPopupState] = useState<PopupState>({ state: "None" });
  const [filterText, setFilterText] = useState('');
  const [transportToRemove, setTransportToRemove] = useState<TransportDto | undefined>(undefined);
  const canModify = usePermission('otherEntitiesModify');
  const siteFilter = useSiteFilter();

  const getTransports = useGetTransports();

  const createClicked = () => {
    displayMessage.clear();
    setPopupState({ state: "AddEditTransport", transport: null });
  }

  const editClicked = (transport: TransportDto) => {
    displayMessage.clear();
    setPopupState({ state: "AddEditTransport", transport });
  }

  const transportUpserted = (transportId: string, transportName: string) => {
    displayMessage.success(`Transport ${transportName} ${popupState.transport ? 'updated' : 'created'}`);
    getTransports.query();
  }

  const editDismissed = () => setPopupState({ state: 'None' });

  const containsFilterText = (transport: TransportDto) => {
    const satisfiesFilter = textSatisfiesFilter(filterText);
    return satisfiesFilter(transport.name) || satisfiesFilter(transport.number);
  };

  const filterTextChanged = (text: string) => setFilterText(text);

  const removeSuccess = () => {
    displayMessage.success(`Transport ${transportToRemove?.name} removed`);
    return getTransports.query();
  };

  const removeRequest = useRemoveTransport(transportToRemove?.id ?? '', {
    onSuccess: removeSuccess,
    onError: (err) => displayMessage.fail(err.message),
    onComplete: () => setTransportToRemove(undefined),
  });

  const visibleTransports = (getTransports.data ?? [])
    .filter(containsFilterText)
    .filter(transport => siteFilter.matches(transport.sites))
    .sort(alphabeticallyByName);

  const loadingRows = getTransports.isLoading || removeRequest.isLoading;

  if (getTransports.isError) return <h3>{getTransports.error?.message}</h3>

  return <Container>
    {popupState.state === 'AddEditTransport' && <CreateEditTransport
      initialTransport={popupState.transport}
      onUpsert={transportUpserted}
      mode={modalMode(canModify, !popupState.transport)}
      onDismiss={editDismissed} />}
    <Confirm
      visible={!!transportToRemove}
      title='Remove transport'
      body={`Are you sure that you want to remove transport ${transportToRemove?.name}?`}
      isLoading={removeRequest.isLoading}
      onDismiss={() => setTransportToRemove(undefined)}
      onConfirm={() => removeRequest.request()} />
    <Row className='justify-content-between mb-2'>
      <h2 className='col-auto ka-blue'>Transports</h2>
      <div className='col-auto d-flex gap-2'>
        <PermissionButton permission="otherEntitiesModify" className='btn-ghost-primary' onClick={createClicked}>Create Transport</PermissionButton>
      </div>
    </Row>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    <form>
      <Row className='mb-3 align-items-center'>
        <div className='col-md-4 mb-2 mb-md-0'>
          <FilterInput placeholder='Search by name or number' onFilterTextChange={filterTextChanged} value={filterText} />
        </div>
        <div className='col-auto d-flex align-items-center'>
          <SiteFilterPill filter={siteFilter} />
        </div>
        <div className='col-auto ms-auto d-flex align-items-center'>
          <PermissionButton permission="otherEntitiesModify" className='btn-ghost-secondary' onClick={() => navigate('/transports/sync')}>Sync from TMX</PermissionButton>
        </div>
      </Row>
    </form>
    <Table bordered className="preserve-whitespace" style={{tableLayout: "fixed"}}>
      <thead>
        <tr>
          <th>Name</th>
          <th className="col-3">Number</th>
          <SiteColumnHeader />
          <ActionsColumnHeader />
        </tr>
      </thead>
      <tbody>
        {!loadingRows && visibleTransports.map(transport =>
          <tr key={transport.id}>
            <td>{transport.name}</td>
            <td>{transport.number}</td>
            <SiteColumnCell sites={transport.sites} />
            <td align="right" className="text-nowrap">
              <div className='dropdown'>
                <Button
                  className='dropdown-toggle btn-ghost-secondary'
                  variant='link'
                  data-bs-toggle='dropdown'><strong>Actions</strong></Button>
                <div className='dropdown-menu'>
                  <EditViewButton
                    canModify={canModify}
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => editClicked(transport)} />
                  <PermissionButton permission="otherEntitiesModify"
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => setTransportToRemove(transport)}>Remove</PermissionButton>
                </div>
              </div>
            </td>
          </tr>)}
        {(loadingRows || visibleTransports.length === 0) &&
          <tr>
            <td colSpan={4} className='text-center text-muted py-3'>
              {loadingRows
                ? <CenteredSpinner />
                : siteFilter.emptyMessage('No transports to show')}
            </td>
          </tr>}
      </tbody>
    </Table>
  </Container>
};

export { TransportPage };
